'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { getTerraGraphCategoryColors } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import { formatAxisLabel, formatTooltipValue } from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface RadarChartProps {
  series: {
    name: string;
    data: number[];
  }[];
  categories: string[];
  title?: string;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  xaxisTitle?: string;
  yaxisTitle?: string;
  tooltipFormatter?: (value: number) => string;
  showLegend?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
}

export function RadarChart({
  series,
  categories,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  xaxisTitle = '',
  yaxisTitle = '',
  tooltipFormatter,
  showLegend = true,
  legendPosition = 'bottom',
}: RadarChartProps) {
  const { mode: themeMode } = useThemeMode();

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'radar',
        toolbar: {
          show: false,
        },
        foreColor: 'currentColor',
      },
      colors: colors,
      title: {
        text: title,
        align: 'left',
      },
      xaxis: {
        categories,
        title: {
          text: xaxisTitle,
        },
      },
      yaxis: {
        title: {
          text: yaxisTitle,
        },
        labels: {
          formatter: formatAxisLabel,
        },
      },
      tooltip: {
        theme: themeMode === 'dark' ? 'dark' : 'light',
        enabled: true,
        y: {
          formatter: tooltipFormatter ?? formatTooltipValue,
        },
      },
      legend: {
        show: showLegend,
        position: legendPosition,
      },
    }),
    [
      colors,
      title,
      categories,
      xaxisTitle,
      yaxisTitle,
      themeMode,
      tooltipFormatter,
      showLegend,
      legendPosition,
    ],
  );

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="radar"
      height={height}
      width={width}
    />
  );
}

export default RadarChart;
